import "./ErrorPanel.scss";

import React, { useCallback, useContext, useEffect, useMemo } from "react";
import clsx from "clsx";

import * as auth from "../../../lib/util/AuthUtils";
import * as strings from "../../../strings";
import * as SupportUtils from "../../../lib/util/SupportUtils";
import * as AnalyticsUtils from "../../../lib/util/AnalyticsUtils";
import * as ErrorUtils from "../../../lib/util/ErrorUtils";

import {
	LocaleContext,
	TextElement,
	translate,
} from "../../../lib/util/LocaleUtils";

import { IconError } from "../../../assets";
import Text from "../Text/Text";
import Button from "../Button/Button";
import SupportLink from "../../custom/SupportLink/SupportLink";
import { Translatable } from "../../../lib/util/Translatable";

const STACKTRACE_ENABLED = true;

export interface ErrorPanelProps {
	error: Error | null;
	content?: TextElement;
	title?: React.ReactNode;
	children?: React.ReactNode;
	className?: string;
}

const ErrorPanel = (props: ErrorPanelProps) => {
	const localeContext = useContext(LocaleContext);
	const { error, content, title, className, children } = props;

	const identifier = error ? ErrorUtils.parseError(error).identifier : null;

	const stack = useMemo(() => {
		let stack = error?.stack;
		if (stack) {
			stack = stack.toString();
		}

		if (stack) {
			stack = stack.toString().split("\n").slice(1).join("\n");
		}
		return stack;
	}, [error]);

	useEffect(() => {
		AnalyticsUtils.sendEvent({
			eventCategory: "Error Panel",
			eventLabel: error?.message,
			eventAction: "error",
			mixpanelProperties: {
				name: "Error Page",
				"Error Message": error?.message,
				"Error Identifier": identifier,
				"Error Stack Trace": stack?.split("\n"),
			},
		});
	}, [error, identifier, stack]);

	const handleChat = useCallback(() => {
		return SupportUtils.contactSupport(
			`Hi, I encountered an error (${identifier}).\n`,
		);
	}, [identifier]);

	if (!error) return null;

	return (
		<div className={clsx("ErrorPanel", className)}>
			<div className={"--row"}>
				<div className={"ErrorPanel__icon-container"}>
					<IconError />
				</div>
				<div>
					<Text tag={"h1"}>
						{title !== undefined ? title : strings.MISC_ERROR}
					</Text>
					{children ? (
						children
					) : (
						<div>
							<Text tag={"p"}>
								{content ? (
									content instanceof Translatable ? (
										content.with({ identifier })
									) : (
										content
									)
								) : (
									<>
										<Text inline={true}>{strings.MISC_ERROR_APOLOGIES}</Text>{" "}
										<SupportLink>
											<Text tag={"p"}>
												{translate(
													strings.MISC_CONTACT_SUPPORT,
													localeContext.locale,
												).toLowerCase()}
											</Text>
										</SupportLink>
									</>
								)}
							</Text>
							<br />
							<Button onClick={handleChat}>Chat with Support</Button>
						</div>
					)}
				</div>
			</div>
			{STACKTRACE_ENABLED && auth.isSuperAdmin() && (
				<>
					<hr />
					<h2>Diagnostic Info</h2>
					<pre className={"ErrorPanel__diagnostic"}>
						<code>
							{error.toString()}
							{stack && (
								<div className={"ErrorPanel__stack"}>
									{((stack as any).componentStack || stack)
										.split("\n")
										.map((line: string, index: number) => (
											<li key={index}>{line.trim()}</li>
										))}
								</div>
							)}
						</code>
					</pre>
				</>
			)}
		</div>
	);
};

export default React.memo(ErrorPanel);
